import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import Table from '@components/Table';
import useSearchParams from '@hooks/useSearchParams';
import { bytesFormat, dateFormat } from '@utils/formatters';
import {
  Button,
  DatePicker,
  message,
  Radio,
  Select,
  Switch,
  Tooltip,
} from 'antd';
import moment from 'moment';
import { IContract } from '@components/DeviceActionModal/types';
import equalObjects from '@utils/equalObjects';
import api from '@services/api';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useCheckRole } from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import ColumnEmails from './ColumnEmails';
import HistoricModal from './HistoricModal';

interface ConfigProps {
  alert100?: boolean;
  alert90?: boolean;
  alert80?: boolean;
  alert70?: boolean;
  alert60?: boolean;
  notificationEmails?: string[];
}

const ConsumptionAlerts = () => {
  const { t } = useTranslation('alerts');

  const [currentConfig, setCurrentConfig] = useState<ConfigProps>();
  const [loadingUpdateConfig, setLoadingUpdateConfig] = useState(false);

  const canManage = useCheckRole({
    role: rolesConstants.MANAGE_CONSUMPTION_ALERTS,
  });

  const updateConfig = useCallback(
    (payload: ConfigProps) =>
      api.put(`/service-proxy/basic-alerts-configuration/`, payload),
    [],
  );

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['activeTab', 'dateFrom'],
    extraParams: ['contractId'],
  });

  const { activeTab, contractId, dateFrom, page, linesPerPage } = useMemo(
    () => searchParams,
    [searchParams],
  );

  const isTabConfig = useMemo(() => activeTab === 'config', [activeTab]);

  const { data: alerts } = useSwr(
    !isTabConfig ? '/service-proxy/basic-alerts' : null,
    {
      params: {
        assetNumber: contractId,
        dateFrom,
        page: page ? Number(page) - 1 : 0,
        linesPerPage: linesPerPage ?? 20,
      },
    },
  );

  const { data: config, mutate: mutateConfig } = useSwr<ConfigProps>(
    canManage && isTabConfig
      ? '/service-proxy/basic-alerts-configuration'
      : null,
  );

  useEffect(() => {
    setCurrentConfig(config);
  }, [config]);

  const data = useMemo(
    () =>
      canManage && isTabConfig
        ? { content: currentConfig ? [currentConfig] : [] }
        : alerts,
    [canManage, isTabConfig, currentConfig, alerts],
  );

  useEffect(() => {
    if (!dateFrom && !isTabConfig) {
      const lastWeek = moment()
        .subtract(3, 'month')
        .startOf('day')
        .format('YYYY-MM-DD[T]HH:mm:ss');

      setSearchParams({
        dateFrom: lastWeek,
      });
    }
  }, [activeTab, dateFrom, isTabConfig, setSearchParams]);

  const { data: contracts } = useSwr<IContract[]>('/service-proxy/contracts');

  const contractsOptions = useMemo(
    () =>
      Array.isArray(contracts)
        ? contracts?.map(({ assetNumber, nickname, planName }) => {
            const labelParts = [assetNumber, nickname, planName].filter(
              Boolean,
            );
            const label = labelParts.join(' - ');

            return {
              label,
              value: assetNumber,
            };
          })
        : [],
    [contracts],
  );

  const renderAlertSwitch = useCallback(
    (checked: boolean, type: number) => (
      <Tooltip title={t(`consumption.${checked ? 'disable' : 'enable'}`)}>
        <Switch
          checked={checked}
          onChange={value => {
            const newValue = { [`alert${type}`]: value };
            setCurrentConfig(crr => (crr ? { ...crr, ...newValue } : newValue));
          }}
        />
      </Tooltip>
    ),
    [t],
  );

  const handleUpdateConfig = useCallback(async () => {
    setLoadingUpdateConfig(true);
    try {
      const {
        alert100,
        alert90,
        alert80,
        alert70,
        alert60,
        notificationEmails,
      } = currentConfig as ConfigProps;

      await updateConfig({
        alert100,
        alert90,
        alert80,
        alert70,
        alert60,
        notificationEmails,
      });
      mutateConfig();
      message.success(t('consumption.configurationUpdatedSuccessfully'));
    } catch {
      message.error(t('consumption.errorUpdatingConfiguration'));
    }
    setLoadingUpdateConfig(false);
  }, [currentConfig, mutateConfig, t, updateConfig]);

  const columns = useMemo(() => {
    if (canManage && isTabConfig) {
      return [
        ...[100, 90, 80, 70, 60].map(value => ({
          title: (
            <span>
              {`${value}%`}
              <Tooltip
                title={t('consumption.notifyWhenConsumptionReaches', { value })}
              >
                <Button
                  type="link"
                  size="small"
                  style={{ marginRight: -4 }}
                  icon={<QuestionCircleOutlined />}
                />
              </Tooltip>
            </span>
          ),
          width: 88,
          align: 'center' as const,
          dataIndex: `alert${value}`,
          render: (alert: boolean) => renderAlertSwitch(alert, value),
        })),
        {
          title: (
            <span>
              {t('consumption.notificationEmails')}
              <Tooltip
                title={`${t('consumption.emailsThatWillBeNotified')} (max: 5)`}
              >
                <Button size="small" icon={<QuestionCircleOutlined />} />
              </Tooltip>
            </span>
          ),
          align: 'center' as const,
          dataIndex: 'notificationEmails',
          render: (values: string[]) => (
            <ColumnEmails
              emails={values}
              onChange={newEmails => {
                setCurrentConfig(crr => ({
                  ...crr,
                  notificationEmails: newEmails,
                }));
              }}
            />
          ),
        },
      ];
    }
    return [
      {
        title: t('consumption.threshold'),
        dataIndex: 'threshold',
        align: 'center' as const,
        render: (value: number) => `${value}%`,
      },
      {
        title: t('consumption.consumption'),
        align: 'center' as const,
        dataIndex: 'consumption',
        render: bytesFormat,
      },
      {
        title: t('consumption.contract'),
        align: 'center' as const,
        dataIndex: 'assetNumber',
        render: (value: string) => (
          <Tooltip title={t('consumption.viewContract')}>
            <Link to={`/financial/contracts?contractId=${value}`}>{value}</Link>
          </Tooltip>
        ),
      },
      {
        title: t('consumption.createdAt'),
        align: 'center' as const,
        dataIndex: 'createdAt',
        render: dateFormat,
      },
    ];
  }, [canManage, isTabConfig, renderAlertSwitch, t]);

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: t('consumption.fromDate'),
        element: (
          <DatePicker
            format="DD/MM/YYYY"
            value={dateFrom ? moment(dateFrom) : undefined}
            disabledDate={date => {
              const today = moment().startOf('day');

              return date.startOf('day').isAfter(today);
            }}
            placeholder=""
            onChange={date => {
              setSearchParams({
                dateFrom: date?.startOf('day').format('YYYY-MM-DD[T]HH:mm:ss'),
              });
            }}
            style={{
              width: '100%',
            }}
            allowClear={false}
          />
        ),
      },
      {
        visible: true,
        label: t('consumption.contract'),
        element: (
          <Select
            options={contractsOptions}
            value={contractId}
            onChange={value => setSearchParams({ contractId: value })}
            style={{ width: '100%' }}
            showSearch
            allowClear
          />
        ),
      },
    ],
    [contractId, contractsOptions, dateFrom, setSearchParams, t],
  );

  const disableSaveButton = useMemo(
    () =>
      config && currentConfig ? equalObjects(config, currentConfig) : true,
    [config, currentConfig],
  );

  return (
    <Table
      title={
        canManage ? (
          <Radio.Group
            optionType="button"
            value={activeTab}
            defaultValue="alerts"
            onChange={({ target: { value } }) => {
              setSearchParams({ activeTab: value });
            }}
          >
            <Radio value="alerts">{t('consumption.alerts')}</Radio>
            <Radio value="config">{t('consumption.config')}</Radio>
          </Radio.Group>
        ) : (
          t('consumption.alerts')
        )
      }
      data={data?.content || []}
      columns={columns}
      loading={isTabConfig ? !config : !alerts}
      tableKey="consumptionAlerts"
      filters={
        !isTabConfig
          ? {
              list: tableFilters,
              hasValuesInFilters: hasValuesInExtraParams,
              openByDefault: true,
              onClearFilters: () => clearSearchParams(),
            }
          : undefined
      }
      onTableChange={setSearchParams}
      noPagination={isTabConfig}
      pagination={{
        page,
        linesPerPage,
        total: data?.totalElements,
      }}
      disableTableConfig={isTabConfig}
      extraActions={
        canManage &&
        isTabConfig && (
          <>
            <Button
              type="primary"
              onClick={handleUpdateConfig}
              disabled={disableSaveButton}
              loading={loadingUpdateConfig}
            >
              {t('consumption.save')}
            </Button>
            <HistoricModal />
          </>
        )
      }
    />
  );
};

export default ConsumptionAlerts;
